/**
 * Read-only organisational risk severity reference tiers for Lead Advisor dashboard.
 * Aligned with selected onboarding point scale (3 / 5 / 10): Likelihood × Impact narrative summary —
 * orthogonal to onboarding’s Severity×Likelihood×Consequence×Control product formula — see Org Setup UI footnote.
 */
import { normalizeRiskScale } from "./riskAnalysisMatrix";

/** @typedef {{ score: number, likelihood: string, impact: string, riskLevel: string, description: string, pillKey: 'g1'|'g2'|'y'|'o'|'r'|'b'|'n' }} RiskSeverityTierRow */

/** @type {Record<'3'|'5'|'10', RiskSeverityTierRow[]>} */
const TIERS_BY_SCALE = {
  "3": [
    {
      score: 1,
      likelihood: "Low",
      impact: "Low",
      riskLevel: "Low",
      description:
        "Limited probability and limited consequence; monitor as part of routine assurance.",
      pillKey: "g1",
    },
    {
      score: 2,
      likelihood: "Moderate",
      impact: "Moderate",
      riskLevel: "Medium",
      description:
        "Plausible occurrence with noticeable disruption; formal response planning recommended.",
      pillKey: "y",
    },
    {
      score: 3,
      likelihood: "High",
      impact: "High",
      riskLevel: "High",
      description:
        "Serious consequence or elevated likelihood; treat as programme priority.",
      pillKey: "r",
    },
  ],
  "5": [
    {
      score: 1,
      likelihood: "Rare",
      impact: "Negligible",
      riskLevel: "Very Low",
      description:
        "Event unlikely to occur; minimal impact on operations or compliance posture.",
      pillKey: "g1",
    },
    {
      score: 2,
      likelihood: "Unlikely",
      impact: "Minor",
      riskLevel: "Low",
      description:
        "Event may occur only in exceptional circumstances; limited disruption, easily remediated.",
      pillKey: "g2",
    },
    {
      score: 3,
      likelihood: "Possible",
      impact: "Moderate",
      riskLevel: "Medium",
      description:
        "Event could occur; noticeable impact requiring planned response and monitoring.",
      pillKey: "y",
    },
    {
      score: 4,
      likelihood: "Likely",
      impact: "Major",
      riskLevel: "High",
      description:
        "Event expected under many circumstances; significant business disruption or regulatory exposure.",
      pillKey: "o",
    },
    {
      score: 5,
      likelihood: "Almost Certain",
      impact: "Critical",
      riskLevel: "Critical",
      description:
        "Event imminent or occurred; severe operational, financial, or reputational damage possible.",
      pillKey: "r",
    },
  ],
  "10": [
    {
      score: 1,
      likelihood: "Very rare",
      impact: "Negligible",
      riskLevel: "Minimal",
      description: "Rare events with negligible business or compliance consequence.",
      pillKey: "g1",
    },
    {
      score: 2,
      likelihood: "Rare",
      impact: "Minor",
      riskLevel: "Very Low",
      description: "Limited exposure; corrective action typically lightweight.",
      pillKey: "g1",
    },
    {
      score: 3,
      likelihood: "Uncommon",
      impact: "Low",
      riskLevel: "Low",
      description: "Manage through existing controls with periodic oversight.",
      pillKey: "g2",
    },
    {
      score: 4,
      likelihood: "Occasional",
      impact: "Moderate-low",
      riskLevel: "Low–Medium",
      description: "May require uplift of monitoring cadence.",
      pillKey: "g2",
    },
    {
      score: 5,
      likelihood: "Possible",
      impact: "Moderate",
      riskLevel: "Medium",
      description: "Documented mitigation with accountable owner and metrics.",
      pillKey: "y",
    },
    {
      score: 6,
      likelihood: "Probable",
      impact: "Moderate-high",
      riskLevel: "Medium–High",
      description: "Expect treatment plan within defined executive tolerance.",
      pillKey: "y",
    },
    {
      score: 7,
      likelihood: "Likely",
      impact: "Major",
      riskLevel: "High",
      description: "Significant remediation and assurance testing expected.",
      pillKey: "o",
    },
    {
      score: 8,
      likelihood: "Highly likely",
      impact: "Severe",
      riskLevel: "Very High",
      description:
        "Programme-level intervention; escalate to steering committee oversight.",
      pillKey: "o",
    },
    {
      score: 9,
      likelihood: "Expected",
      impact: "Critical",
      riskLevel: "Severe",
      description:
        "Urgent remediation; contingency and regulator communications readiness required.",
      pillKey: "r",
    },
    {
      score: 10,
      likelihood: "Imminent",
      impact: "Catastrophic",
      riskLevel: "Critical",
      description:
        "Existential disruption to objectives; mobilise crisis management and exec leadership.",
      pillKey: "r",
    },
  ],
};

/**
 * Banner pill colours — left→right ascending severity hues.
 * @returns {string[]} Tailwind bg classes length N
 */
export function riskScaleBannerPillClasses(scale) {
  const s = normalizeRiskScale(scale);
  if (s === "3") return ["bg-emerald-500", "bg-amber-400", "bg-rose-600"];
  if (s === "5")
    return [
      "bg-emerald-500",
      "bg-lime-400",
      "bg-amber-400",
      "bg-orange-500",
      "bg-rose-600",
    ];
  return [
    "bg-emerald-500",
    "bg-emerald-400",
    "bg-lime-400",
    "bg-lime-300",
    "bg-amber-400",
    "bg-amber-500",
    "bg-orange-500",
    "bg-orange-600",
    "bg-rose-500",
    "bg-rose-600",
  ];
}

/** Risk-level badge styling (risk level pills in table). */
export function riskLevelPillClasses(pillKey) {
  const map = {
    g1: "bg-emerald-100 text-emerald-900 border-emerald-300",
    g2: "bg-lime-100 text-lime-900 border-lime-300",
    y: "bg-amber-100 text-amber-950 border-amber-300",
    o: "bg-orange-100 text-orange-950 border-orange-300",
    r: "bg-rose-100 text-rose-950 border-rose-300",
    b: "bg-sky-100 text-sky-950 border-sky-300",
    n: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[pillKey] || map.n;
}

/**
 * @param {string | number | undefined} scale
 * @returns {RiskSeverityTierRow[]}
 */
export function getRiskSeverityReferenceTiers(scale) {
  const s = normalizeRiskScale(scale);
  return TIERS_BY_SCALE[s] ?? TIERS_BY_SCALE["5"];
}
